<template>
  <div
  class="tag-input p-inputtext form-control position-relative d-flex flex-wrap align-items-center py-0 m-0"
  :class="{
		'p-disabled bg-light': disabled,
		'px-1': modelValue && modelValue.length
	}"
    :style="{ cursor: disabled ? 'default' : 'text' }"
  >
    <ul role="listbox" aria-orientation="horizontal" class="d-flex flex-wrap align-items-center w-100 h-auto">
      <li
        v-for="(item, itemIndex) in modelValue"
        :key="item.name"
		role="option"
		aria-selected="true"
		:aria-setsize="modelValue.length"
		:aria-posinset="1+ itemIndex"
        class="d-flex align-items-center h-100"
      >
        <AppChip
          :text="item"
          :class="itemClass"
          removable
          @remove="removeTag(itemIndex)"
        >
          <slot
            name="item"
            v-bind="{ item, itemIndex }"
          >
            {{ item }}
          </slot>
        </AppChip>
      </li>
      <li role="option" class="d-autocomplete-input-chip">
        <input
          :id="inputId"
          v-model.trim="entry"
          autocomplete="off"
          type="text"
          class="w-100 p-0 border-0"
          :class="{ 'invisible': disabled }"
          @input="onInput"
          @keydown="characterLimit && handlecharacterLimit($event)"
          @keydown.enter="onEnter"
          @keydown.delete="removeLastTag"
          @keydown.space="handleWhitespace"
          @focusout="onFocusOut"
          @blur="onBlur"
          @paste="onPaste"
          v-bind="{...inputProps}"
          :disabled="disabled"
        />
      </li>
    </ul>

    <AppAutocomplete
      v-if="suggestions"
      v-show="isTyping || (isFocused && !isEmpty(entry))"
      :list="without(suggestions, ...modelValue)"
      :search-query="entry"
      :keys="displayedKey"
      @select="onSelect"
    />
  </div>
</template>

<script>
import AppChip from "./AppChip.vue";
import AppAutocomplete from "./AppAutocomplete.vue";
import { without, isEmpty } from "lodash-es";
import $ from 'jquery'

export default {
  components: {
    AppAutocomplete,
    AppChip
  },

  emits: [
    "update:modelValue",
    "item-select",
    "item-add",
    "item-remove"
  ],

  props: {
    inputId: String,
    modelValue: Array,
    suggestions: Array,
    displayedKey: [String, Array],
    transform: Function,
    itemClass: String,
    characterLimit: [Number, null],
    addOnBlur: {
        type: Boolean,
        default: false
	},
    inputProps: {
      ariaLabel: String
    },
	disabled: {
		type: Boolean,
		default: false
	}
  },

  data() {
    return {
      isFocused: false,
      isTyping: false,
      entry: "",
      htmlInputElement: null
    };
  },

  methods: {
    without,
    isEmpty,

    pushTag(value) {
      this.$emit("item-add", value);
      this.$emit("update:modelValue", [...this.modelValue, this.transform
        ? this.transform(value)
        : value
      ]);
    },

    removeTag(index) {
      const removedItem = this.modelValue.toSpliced(index, 1)[0];
      this.$emit("item-remove", removedItem);
      this.$emit("update:modelValue", this.modelValue.filter((_, i) => i !== index));
    },

    onEnter(event) {
      event.preventDefault();

      if (this.entry === "") {
        return;
      }

      const isDuplicate = this.modelValue.findIndex((item) => item[this.displayedKey] === this.entry || item === this.entry) > -1;
      if (isDuplicate) {
        return;
      }

      const existing = this.suggestions?.find((suggestion) =>
        suggestion === this.entry || suggestion[this.displayedKey] === this.entry
      );

      if (existing) {
        this.pushTag(existing);
      } else {
		this.pushTag(this.entry);
      }

      this.entry = "";
    },

    removeLastTag() {
      if (this.entry.length === 0) {
        this.removeTag(this.modelValue.length - 1);
      }
    },

    onInput() {
      this.isTyping = true;
    },

    onSelect($event) {
      this.pushTag($event);
      this.entry = "";
    },

    onPaste(event) {
      let pastedText = (event.clipboardData || window.clipboardData).getData("text");
      let tagsToCreate = pastedText.match(/(?<=-\t).+/gm);

      if (tagsToCreate) {
        tagsToCreate.forEach(tagName => this.pushTag({ tagName }));
        event.preventDefault();
      }
      else if (this.characterLimit && pastedText.length > this.characterLimit) {
        $(event.target).tooltip('show');
        event.preventDefault();
      }
    },

    resize(input) {
      input.style.height = input.scrollHeight + "px";
    },

    onFocusOut({ target: inputElement }) {
      this.isFocused = false;
      this.isTyping = false;
      $(inputElement).tooltip('hide');
    },

	onBlur(e) {
		if (this.addOnBlur) {
			this.onEnter(e);
		}
	},

    handleWhitespace(event) {
      (event.target.value.slice(-1) === ' ' || event.target.value.length === this.characterLimit) && event.code === "Space" && event.preventDefault();
    },

    handlecharacterLimit(event) {
      const { target: inputElement, code } = event;

      if (inputElement.value.length >= this.characterLimit && !["Enter", "Backspace", "Delete"].includes(code)
      ) {
        $(inputElement).tooltip('show');
        event.preventDefault();
      } else {
        $(inputElement).tooltip('hide');
      }
    },

    initCharacterLimitTooltip() {
      $(this.htmlInputElement).tooltip({
        title: "Maximum characters reached (max. 45)",
        trigger: "manual",
      });
    }
  },

  mounted() {
    this.htmlInputElement = this.$el.querySelector('input[type="text"]');
    this.initCharacterLimitTooltip();
  }
};
</script>

<style scoped>
ul {
	gap: calc(0.5rem / 2);
}

.d-autocomplete-input-chip {
  flex: 1 1 auto;
  display: inline-flex;
}

.form-control {
  min-height: calc(1.5em + 0.75rem + 2px);
  height: auto;
}

.form-control:focus-within {
  outline: 0 none;
  outline-offset: 0;
  box-shadow: 0 0 0 1px #6366F1;
  border-color: #4F46E5;
}
</style>
